import React from "react";
import { useGlobalHeaderConfig } from "src/hooks/useGlobalHeaderConfig";
import goto from "@pageDefinitions/goto";
import BackButton from "@components/linear-buyflow/BackButton";
import NoomWhiteLogo from "./images/NoomMedWordmarkWhite.svg";
import NoomBlackLogo from "./images/NoomMedWordmark.svg";
import ProjectCalorieLogo from "./images/ProjectCalorieLogo.svg";
import ProjectCalorieLogoWhite from "./images/ProjectCalorieLogoWhite.svg";
import { GlobalHeaderConfig } from "@pageDefinitions/types/GlobalHeaderConfig";
import { zIndex } from "src/design-system/styles/zIndices";
import GenericHeader from "@components/header/GenericHeader";
import styled from "@emotion/styled";
import BlackCloseButton from "./images/Close.svg";
import WhiteCloseButton from "./images/whiteXButton.svg";
import { compassColors } from "@utils/styling";
import { css, Global } from "@emotion/react";
import { trackBuyflowEvent } from "@utils/api/tracker";
import { useGotoOptions } from "src/hooks/pageSet";
import { breakpointDesktopOnly } from "src/design-system/styles/breakpoints";

const GLOBAL_HEADER_HEIGHT = "45px";

export const GLOBAL_HEADER_LOGO_MAPPINGS = {
  DARK: {
    PROJECT_CALORIE: ProjectCalorieLogoWhite,
    DEFAULT: NoomWhiteLogo,
  },
  LIGHT: {
    PROJECT_CALORIE: ProjectCalorieLogo,
    DEFAULT: NoomBlackLogo,
  },
};

const BackButtonContainer = styled.div`
  @media ${breakpointDesktopOnly} {
    padding-left: calc(8rem + 10vw);
  }
`;

function BackButtonWrapper({
  invertedColors,
  onClickBack,
  theme,
}: Pick<GlobalHeaderConfig, "invertedColors" | "theme"> & {
  onClickBack?: () => void;
}) {
  return (
    <BackButtonContainer>
      <BackButton
        backArrowColor={invertedColors ? "white" : "default"}
        backArrowType={theme === "DEFAULT" ? "noom" : "calorie"}
        onClickBack={onClickBack}
      />
    </BackButtonContainer>
  );
}

const ExitIcon = styled.img`
  width: 24px;
  padding: 7px 10px 7px 10px;
`;

function ExitButton({
  invertedColors,
  exitAction,
}: Pick<GlobalHeaderConfig, "exitAction" | "invertedColors">) {
  function performExitAction() {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "exitButton",
    });
    Object.keys(exitAction).forEach((key) => {
      goto[key](...exitAction[key]);
    });
  }

  if (!exitAction) {
    return null;
  }

  return (
    <ExitIcon
      src={invertedColors ? WhiteCloseButton : BlackCloseButton}
      onClick={() => performExitAction()}
    />
  );
}

const StyledGlobalHeader = styled(GenericHeader)<{
  sticky?: GlobalHeaderConfig["sticky"];
}>`
  top: 0;
  height: ${GLOBAL_HEADER_HEIGHT};
  ${({ sticky }) =>
    sticky &&
    css`
      position: fixed;
    `}

  z-index: ${zIndex.globalHeader}
`;

function getLogo(
  theme: GlobalHeaderConfig["theme"],
  invertedColors: GlobalHeaderConfig["invertedColors"]
): string {
  if (invertedColors) {
    return (
      GLOBAL_HEADER_LOGO_MAPPINGS.DARK[theme] ||
      GLOBAL_HEADER_LOGO_MAPPINGS.DARK.DEFAULT
    );
  }
  return (
    GLOBAL_HEADER_LOGO_MAPPINGS.LIGHT[theme] ||
    GLOBAL_HEADER_LOGO_MAPPINGS.LIGHT.DEFAULT
  );
}

function Header({
  enabled,
  sticky,
  exitAction,
  invertedColors,
  theme,
  hideBackButton,
  hideExitButton,
  onClickBack,
}: GlobalHeaderConfig & {
  onClickBack?: () => void;
}) {
  if (!enabled) {
    return null;
  }

  return (
    <>
      <Global
        styles={css`
          html {
            overscroll-behavior-y: none;
          }
        `}
      />
      <StyledGlobalHeader
        backgroundColor={compassColors.white}
        sticky={sticky}
        rightSide={
          !hideExitButton && (
            <ExitButton
              exitAction={exitAction}
              invertedColors={invertedColors}
            />
          )
        }
        leftSide={
          !hideBackButton && (
            <BackButtonWrapper
              theme={theme}
              invertedColors={invertedColors}
              onClickBack={onClickBack}
            />
          )
        }
      >
        <img src={getLogo(theme, invertedColors)} alt="Logo" />
      </StyledGlobalHeader>
    </>
  );
}

export function GlobalHeader() {
  const gotoOptions = useGotoOptions();
  const config = useGlobalHeaderConfig();

  function onClickBack() {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "backButton",
    });
    gotoOptions?.history?.goBack();
  }

  return (
    <>
      <Header {...config} onClickBack={onClickBack} />
    </>
  );
}

export function GlobalHeaderEmptySpacing() {
  const { sticky } = useGlobalHeaderConfig();
  if (!sticky) {
    return null;
  }
  return <div css={{ minHeight: GLOBAL_HEADER_HEIGHT }} />;
}
